import * as React from 'react';
import classnames from 'classnames';
import { camelCase } from 'lodash';
import { connect } from 'react-redux';

import CreatorInfoWithChannels from 'components/creator-info-with-channels/CreatorInfoWithChannels';
import DeliverableTagPill from 'components/deliverable-tag-pill/DeliverableTagPill';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import DeliverableTab from 'modules/deliverables/components/deliverable-tab/DeliverableTab';
import DeliverableMediaStatusMark from 'modules/deliverables/components/media/DeliverableMediaStatusMark';

import { __t, applyTranslations } from 'utils/language';
import {
  DELIVERABLE_ICONS,
  DELIVERABLE_TAB_INFORMATION,
  DELIVERABLE_TAB_MEDIA,
  DELIVERABLE_TYPE_CONTENT_PRODUCTION,
  getDeliverableMediaStatus
} from 'utils/deliverables';
import { isAccepted } from 'utils/subcampaign';
import { getDisplayDate, getDisplayDateRange } from 'utils/date';

import type { Deliverable, DeliverableTabKey, InfluencerMetrics, Subcampaign, SubcampaignInfluencerProfile } from 'types';

import './deliverable-header.scss';

import type { SyntheticEvent } from 'react';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./deliverable-header.i18n.json'));
applyTranslations(require('../deliverable-edit-common/deliverable-edit-common.i18n.json'));

type OwnProps = {
  deliverable: Deliverable,
  offerId: number,
  subcampaign: Subcampaign,
  influencerProfile?: SubcampaignInfluencerProfile | null,
  hasChanges?: boolean,
  selectedTab: DeliverableTabKey,
  isForBrand: boolean,
  isDisabled: boolean,
  hideTabs?: boolean,
  onTabChanged?: (a: SyntheticEvent<HTMLDivElement>) => void
};

type StateProps = {
  influencersMetrics: InfluencerMetrics
}

type Props = OwnProps & StateProps;

const COMMON_MSG_KEY = 'collabary_common';

class DeliverableHeader extends React.Component<Props> {
  onClick = (event: SyntheticEvent<HTMLDivElement>): void => {
    const { isDisabled, onTabChanged } = this.props;

    if (!isDisabled && onTabChanged) {
      onTabChanged(event);
    }
  };

  renderTabs(): React.ReactNode | undefined | null {
    const {
      offerId,
      subcampaign,
      deliverable,
      hasChanges,
      selectedTab,
      isForBrand,
      isDisabled,
      hideTabs
    } = this.props;

    if (hideTabs || hasChanges || !isAccepted(subcampaign) || subcampaign.offer.id !== offerId) {
      return null;
    }

    return (
      <div className="deliverable-header__tabs">
        <DeliverableTab
          name={DELIVERABLE_TAB_INFORMATION}
          label={__t('deliverable_header.tabs.information')}
          isSelected={selectedTab === DELIVERABLE_TAB_INFORMATION}
          isDisabled={isDisabled}
          onClick={this.onClick}
          data={{ qa: 'deliverable-header-tab__information' }}
        />
        <DeliverableTab
          name={DELIVERABLE_TAB_MEDIA}
          label={(
            <React.Fragment>
              <span>{__t(`deliverable.tabs.media_${isForBrand ? 'brand' : 'influencer'}`)}</span>
              <DeliverableMediaStatusMark
                isForBrand={isForBrand}
                requiresApproval={deliverable.deliverableType === DELIVERABLE_TYPE_CONTENT_PRODUCTION}
                status={getDeliverableMediaStatus(deliverable, isForBrand)}
              />
            </React.Fragment>
          )}
          isSelected={selectedTab === DELIVERABLE_TAB_MEDIA}
          isDisabled={isDisabled}
          onClick={this.onClick}
          data={{ qa: 'deliverable-header-tab__add-content' }}
        />
      </div>
    );
  }

  renderType(): React.ReactNode {
    const { deliverable } = this.props;

    const deliverableType = deliverable.deliverableType;

    return (
      <div
        className="deliverable-header__type"
        title={__t(`${COMMON_MSG_KEY}.${deliverableType}.singular`)}
      >
        <div className="deliverable-header__type-icon">
          <SVGDynamicIcon
            name={`${DELIVERABLE_ICONS[camelCase(deliverableType)]}`}
            height={20}
            width={20}
            customColor="black"
          />
        </div>
        <div className="deliverable-header__type-label">
          {__t(`deliverable_header.icon_label.${deliverableType}`)}
        </div>
      </div>
    );
  }

  renderTitle(): React.ReactNode {
    const { deliverable } = this.props;

    return (
      <Tooltip
        message={deliverable.name}
        display="flex"
      >
        <div
          className="deliverable-header__title text-overflow-ellipsis"
          data-hj-suppress
        >
          {deliverable.name}
        </div>
      </Tooltip>
    );
  }

  renderTag(): React.ReactNode {
    const { deliverable } = this.props;
    const tag = deliverable.tag;

    if (!tag || !tag.name || !tag.color) {
      return null;
    }

    return (
      <div className="deliverable-header__tag">
        <DeliverableTagPill
          name={tag.name}
          color={tag.color}
        />
      </div>
    );
  }

  renderDate(): React.ReactNode {
    const { deliverable } = this.props;
    const deliverableDates = getDisplayDateRange(deliverable.startDate, deliverable.endDate);

    return (
      <div
        className="deliverable-header__dates text-overflow-ellipsis"
        title={deliverableDates}
      >
        {deliverableDates}
      </div>
    );
  }

  render(): React.ReactNode {
    const { hasChanges, deliverable, influencerProfile, influencersMetrics } = this.props;
    const startDate = getDisplayDate(deliverable.startDate);
    const endDate = getDisplayDate(deliverable.endDate);

    const headerClasses = classnames({
      'deliverable-header': true,
      'deliverable-header--short': !influencerProfile,
      'deliverable-header--changed': hasChanges
    });

    return (
      <div className={headerClasses}>
        {
          influencerProfile && influencersMetrics[influencerProfile.id] && (
            <div className="deliverable-header__influencer">
              <CreatorInfoWithChannels
                creatorProfile={influencerProfile}
                metrics={influencersMetrics[influencerProfile.id]}
                includeRatio
              />
            </div>
          )
        }
        <div className="deliverable-header__container">
          {this.renderType()}
          <div className="deliverable-header__title-container">
            <div className="deliverable-header__title-info">
              {this.renderTitle()}
              {this.renderTag()}
            </div>
            {startDate && endDate && this.renderDate()}
          </div>
        </div>
        {this.renderTabs()}
      </div>
    );
  }
}

const mapStateToProps = ({ influencersMetrics }): StateProps => ({
  influencersMetrics: influencersMetrics.data
});

export default connect(mapStateToProps)(DeliverableHeader);
